import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'

// Email Digest (2026-10-19): the header control left of "Reload Site".
// One "Emails" menu (Chris, 2026-10-28 — it was two pills, the header needed
// the room) holding:
//   the Email Digest checkbox  master switch — off = nothing is ever sent.
//   "Select emails…"           popup with a checkbox per digest the user may receive
//                              (filtered server-side by their groups), Save, and a
//                              "Send me a test" per digest.
// Settings live in app.DigestSettings via /api/me/digest; the sends are
// Sync Hub jobs calling /api/internal/digests-send. Default: off, nothing ticked.

type DigestKey = 'daily' | 'weekly' | 'ytd' | 'weekly_sheet' | 'usage'
// Weekly Sheet options (2026-10-23): which week the Monday email covers and in which layout.
interface WsOptions { week: 'current' | 'next' | 'both'; layout: 'pdf' | 'guard' | 'both' }
interface Settings {
  enabled: boolean
  selections: Record<DigestKey, boolean>
  options: { weekly_sheet: WsOptions }
  available: { key: DigestKey; label: string; description: string; cadence: string }[]
}

const GREEN = '#0b3d2e'

export function EmailDigestControls({ hasAccess }: { hasAccess: boolean }) {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Record<DigestKey, boolean> | null>(null)
  const [wsDraft, setWsDraft] = useState<WsOptions>({ week: 'current', layout: 'pdf' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testState, setTestState] = useState<Record<string, string>>({})
  const [menu, setMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // The menu closes on Escape or a click outside it.
  useEffect(() => {
    if (!menu) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false) }
    const onDown = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(false) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [menu])

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/me/digest')
      if (!r.ok) return
      setSettings((await r.json()) as Settings)
    } catch { /* header control — stay quiet */ }
  }, [])
  useEffect(() => { if (hasAccess) void load() }, [hasAccess, load])

  const save = async (body: { enabled?: boolean; selections?: Record<DigestKey, boolean>; options?: { weekly_sheet: WsOptions } }) => {
    setBusy(true); setError(null)
    try {
      const r = await fetch('/api/me/digest', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const j = (await r.json().catch(() => ({}))) as Settings & { error?: string }
      if (!r.ok) throw new Error(j.error || `Save failed (${r.status})`)
      setSettings(j)
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      return false
    } finally { setBusy(false) }
  }

  const sendTest = async (key: DigestKey) => {
    setTestState((s) => ({ ...s, [key]: 'Sending…' }))
    try {
      const r = await fetch(`/api/me/digest/test?digest=${key}`, { method: 'POST' })
      const j = (await r.json().catch(() => ({}))) as { error?: string; sent_to?: string }
      if (!r.ok) throw new Error(j.error || `Failed (${r.status})`)
      setTestState((s) => ({ ...s, [key]: `Sent to ${j.sent_to ?? 'you'}` }))
    } catch (e) {
      setTestState((s) => ({ ...s, [key]: e instanceof Error ? e.message : String(e) }))
    }
  }

  if (!hasAccess || !settings) return null
  const on = settings.enabled
  const picked = Object.values(settings.selections).filter(Boolean).length

  const openModal = () => { setMenu(false); setDraft({ ...settings.selections }); setWsDraft({ ...(settings.options?.weekly_sheet ?? { week: 'current', layout: 'pdf' }) }); setError(null); setTestState({}); setOpen(true) }
  const sel: CSSProperties = { font: 'inherit', fontSize: '0.78rem', padding: '3px 6px', border: '1px solid #ccc', borderRadius: 6, background: '#fff' }

  const modal = open && draft && (
    <div role="dialog" aria-modal="true" aria-label="Select emails" onClick={() => setOpen(false)}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', color: '#1a1a1a', borderRadius: 10, width: 'min(640px, 100%)', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', fontSize: '0.9rem' }}>
        <div style={{ background: GREEN, color: '#f7f3ea', padding: '12px 18px', borderRadius: '10px 10px 0 0' }}>
          <strong style={{ fontSize: '1.05rem' }}>Email digests</strong>
          <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>Tick the emails you want. They are only sent while the Email Digest switch is <b>On</b>.</div>
        </div>
        <div style={{ padding: '10px 18px' }}>
          {!on && <p style={{ margin: '4px 0 10px', padding: '6px 10px', background: '#fff6df', border: '1px solid #e7cf95', borderRadius: 6, fontSize: '0.8rem' }}>Your Email Digest is currently <b>Off</b> — nothing will be sent until you tick Email Digest in the Emails menu in the header.</p>}
          {settings.available.length === 0 && <p>No digests are available for your access level.</p>}
          {settings.available.map((d) => (
            <label key={d.key} style={{ display: 'grid', gridTemplateColumns: '24px 1fr auto', gap: 10, alignItems: 'start', padding: '10px 0', borderBottom: '1px solid #e5e1d6', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!draft[d.key]} onChange={(e) => setDraft({ ...draft, [d.key]: e.target.checked })} style={{ marginTop: 3, width: 18, height: 18 }} />
              <span>
                <strong>{d.label}</strong> <span style={{ color: '#6b6b6b', fontSize: '0.78rem' }}>· {d.cadence}</span>
                <div style={{ color: '#444', fontSize: '0.8rem', marginTop: 2 }}>{d.description}</div>
                {d.key === 'weekly_sheet' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 6, fontSize: '0.78rem' }} onClick={(e) => e.preventDefault()}>
                    <span>Week:
                      <select value={wsDraft.week} onChange={(e) => setWsDraft({ ...wsDraft, week: e.target.value as WsOptions['week'] })} style={{ ...sel, marginLeft: 4 }} aria-label="Which week">
                        <option value="current">This week</option><option value="next">Next week</option><option value="both">Both weeks</option>
                      </select>
                    </span>
                    <span>Layout:
                      <select value={wsDraft.layout} onChange={(e) => setWsDraft({ ...wsDraft, layout: e.target.value as WsOptions['layout'] })} style={{ ...sel, marginLeft: 4 }} aria-label="Which layout">
                        <option value="pdf">Standard sheet (PDF)</option><option value="guard">Guard Sheet layout (Excel)</option><option value="both">Both</option>
                      </select>
                    </span>
                    <span style={{ color: '#6b6b6b' }}>Save, then "Send me a test" to see it.</span>
                  </div>
                )}
                {testState[d.key] && <div style={{ fontSize: '0.76rem', marginTop: 3, color: testState[d.key].startsWith('Sent') ? '#1f6e54' : '#a33' }}>{testState[d.key]}</div>}
              </span>
              <button type="button" onClick={(e) => { e.preventDefault(); void sendTest(d.key) }} disabled={testState[d.key] === 'Sending…'}
                title="Email this digest to you now, so you can see what it looks like"
                style={{ background: '#fff', color: GREEN, border: `1px solid ${GREEN}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Send me a test</button>
            </label>
          ))}
          {error && <p style={{ color: '#a33', fontSize: '0.82rem' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 0 6px' }}>
            <button type="button" onClick={() => setOpen(false)} style={{ background: '#fff', color: GREEN, border: `1px solid ${GREEN}`, borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 700 }}>Cancel</button>
            <button type="button" disabled={busy} onClick={async () => { if (await save({ selections: draft, options: { weekly_sheet: wsDraft } })) setOpen(false) }}
              style={{ background: GREEN, color: '#fff', border: `1px solid ${GREEN}`, borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontWeight: 700 }}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  )

  const btn: CSSProperties = { font: 'inherit', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.02em', borderRadius: 9999, padding: '0.15rem 0.6rem', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.35)', background: 'transparent', color: 'var(--nymr-cream)' }
  return (
    <div ref={menuRef} style={{ position: 'relative', marginRight: '0.35rem' }}>
      <button type="button" onClick={() => setMenu(!menu)} aria-haspopup="menu" aria-expanded={menu}
        title={on ? `Email Digest is on — ${picked} selected` : 'Email Digest is off — nothing is sent'}
        style={{ ...btn, ...(on ? { background: 'var(--nymr-accent)', color: 'var(--nymr-charcoal)', borderColor: 'var(--nymr-accent)' } : {}) }}>
        ✉ Emails ▾
      </button>
      {menu && (
        <div role="menu" aria-label="Emails"
          style={{ position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0, zIndex: 1000, minWidth: 250, background: 'var(--nymr-cream)', color: 'var(--nymr-charcoal)', border: '1px solid var(--nymr-border)', borderTop: '3px solid var(--nymr-accent)', borderRadius: 8, boxShadow: '0 12px 30px rgba(11,61,46,0.25)', padding: '0.6rem 0.7rem', fontSize: '0.84rem' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: busy ? 'wait' : 'pointer' }}>
            <input type="checkbox" checked={on} disabled={busy} onChange={() => void save({ enabled: !on })} style={{ width: 16, height: 16, marginTop: 2 }} />
            <span>
              <strong>Email Digest</strong>
              <span style={{ display: 'block', color: 'var(--nymr-grey)', fontSize: '0.76rem' }}>
                {on ? `On — ${picked} email${picked === 1 ? '' : 's'} selected` : 'Off — nothing is sent'}
              </span>
            </span>
          </label>
          <button type="button" onClick={openModal}
            style={{ marginTop: 8, width: '100%', background: '#fff', color: GREEN, border: `1px solid ${GREEN}`, borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem' }}>
            Select emails{picked ? ` (${picked})` : ''}…
          </button>
          {error && <p style={{ color: '#a33', fontSize: '0.78rem', margin: '6px 0 0' }}>{error}</p>}
        </div>
      )}
      {modal}
    </div>
  )
}
